import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import { useToast, ToastContainer } from '../../components/Toast';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaEye, 
  FaFileAlt, 
  FaMoneyBillWave, 
  FaPhone, 
  FaEnvelope, 
  FaArrowLeft,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaWhatsapp,
  FaLink,
  FaHeart
} from 'react-icons/fa';
import '../assets/css/jobdetail-overrides.css';
import './ModernJobDetails.css';

function getAuthHeader() {
  try {
    // Essayer d'abord la structure principale du localStorage
    const authData = localStorage.getItem('authData');
    if (authData) {
      const data = JSON.parse(authData);
      if (data?.token && data?.tokenType) {
        return { Authorization: `${data.tokenType} ${data.token}` };
      }
    }

    // Fallback sur d'autres clés possibles
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
    for (const storage of storages) {
      for (const key of keys) {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data?.token) return { Authorization: `${data.tokenType || 'Bearer'} ${data.token}` };
      }
    }
    return {};
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return {};
  }
}

const JobDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();
  const id = searchParams.get('id');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [similarJobs, setSimilarJobs] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [checkingSavedStatus, setCheckingSavedStatus] = useState(false);

  useEffect(() => {
    if (!id) {
      setError("Aucune offre sélectionnée.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const config = { headers: { Accept: 'application/json', ...getAuthHeader() } };
        const resp = await axiosClient.get(`/posts/${id}`, config);
        const data = resp?.data?.result || resp?.data || {};
        setJob(data);
        setError('');
      } catch (e) {
        console.error('[JobDetails] fetch error:', e);
        const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
        setError(`Impossible de charger l'offre: ${msg}`);
      } finally {
        setLoading(false);
      }
    })();
    // Fonction pour charger les offres similaires
    const fetchSimilarJobs = async () => {
      try {
        console.log('Fetching similar jobs for id:', id);
        const url = `/posts?op=similar&postId=${id}`;
        console.log('API URL:', url);
        const config = { 
          headers: { 
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...getAuthHeader() 
          },
          withCredentials: true
        };
        
        console.log('Request config:', config);
        const response = await axiosClient.get(url, config);
        console.log('Similar jobs response status:', response.status);
        console.log('Similar jobs data:', response.data);
        
        // Vérifier la structure de la réponse - essayer différentes structures possibles
        let jobs = response.data?.result?.data || response.data?.data || response.data?.result || response.data || [];
        
        // Si jobs n'est pas un tableau, essayer d'extraire le tableau
        if (!Array.isArray(jobs)) {
          if (jobs && Array.isArray(jobs.data)) {
            jobs = jobs.data;
          } else {
            jobs = [];
          }
        }
        
        console.log('Extracted jobs:', jobs);
        setSimilarJobs(jobs);
      } catch (error) {
        console.error('Error fetching similar jobs:', error);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        console.error('Error message:', error.message);
        setSimilarJobs([]); // S'assurer que similarJobs est un tableau vide en cas d'erreur
      }
    };

    if (id) {
      fetchSimilarJobs();
    }
  }, [id]);

  // Fonction pour vérifier si le poste est déjà sauvegardé
  const checkIfJobIsSaved = async (jobId) => {
    if (!jobId) return;
    
    setCheckingSavedStatus(true);
    try {
      const authHeaders = getAuthHeader();
      
      if (!authHeaders.Authorization) {
        setIsSaved(false);
        return;
      }

      // Utiliser axiosClient qui pointe vers l'API de production
      const response = await axiosClient.get('/savedPosts', {
        params: {
          country_code: 'SN',
          embed: 'post,user',
          sort: 'created_at',
          perPage: 50
        }
      });
      
      const data = response.data;
      let savedList = data?.result?.data || data?.data || data?.result || data || [];
      if (!Array.isArray(savedList)) {
        if (savedList && Array.isArray(savedList.data)) savedList = savedList.data;
        else savedList = [];
      }
      
      // Vérifier si le poste actuel est dans la liste des sauvegardés
      const isJobSaved = savedList.some(item => {
        const savedPostId = item.post_id || item.post?.id || item.id;
        return savedPostId == jobId; // Utiliser == pour gérer les types différents
      });
      
      setIsSaved(isJobSaved);
    } catch (e) {
      console.error('[CheckSavedStatus] error:', e);
      setIsSaved(false);
    } finally {
      setCheckingSavedStatus(false);
    }
  };

  // Vérifier l'état de sauvegarde quand le job est chargé
  useEffect(() => {
    if (job?.id) {
      checkIfJobIsSaved(job.id);
    }
  }, [job?.id]);

  const handleSaveJob = async () => {
    if (!job?.id || savingJob) return;
    
    try {
      setSavingJob(true);
      const authHeaders = getAuthHeader();
      
      if (!authHeaders.Authorization) {
        alert('Vous devez être connecté pour sauvegarder une offre');
        return;
      }
      
      if (isSaved) {
        // Retirer des favoris
        const response = await axiosClient.delete(
          `/savedPosts/${job.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              ...authHeaders
            }
          }
        );

        if (response.status === 200 || response.status === 204) {
          setIsSaved(false);
          console.log('Offre retirée des favoris avec succès');
          showToast('Offre retirée des favoris', 'info');
        } else {
          alert('Erreur lors de la suppression');
        }
      } else {
        // Ajouter aux favoris
        const response = await axiosClient.post(
          '/savedPosts',
          {
            post_id: job.id
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              ...authHeaders
            }
          }
        );

        if (response.status === 200 || response.status === 201) {
          setIsSaved(true);
          console.log('Offre sauvegardée avec succès:', response.data);
          showToast('Offre ajoutée aux favoris !', 'success');
        } else {
          alert('Erreur lors de la sauvegarde');
        }
      }
    } catch (error) {
      console.error('Error saving job:', error);
      
      if (error.response?.status === 401) {
        alert('Session expirée. Veuillez vous reconnecter.');
      } else if (error.response?.status === 409) {
        alert('Cette offre est déjà sauvegardée');
        setIsSaved(true);
      } else {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde de l\'offre';
        alert(errorMessage);
      }
    } finally {
      setSavingJob(false);
    }
  };

  const handleApply = () => {
    if (!id) return;
    navigate(`/consultant/job-apply?id=${encodeURIComponent(id)}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatText = (text) => {
    if (!text) return '';
    return text.split('\n').map((paragraph, index) => 
      paragraph.trim() ? <p key={index} className="mb-3">{paragraph}</p> : null
    );
  };

  const logo = (() => {
    let logoUrl = '';
    if (job?.logo_url) {
      // Utiliser logo_url qui contient déjà l'URL complète du backend
      logoUrl = job.logo_url.large || job.logo_url.full || job.logo_url.medium || job.logo_url.small;
    } else if (job?.logo) {
      // Fallback: construire l'URL avec le domaine de production
      logoUrl = `https://update.senoffre.com/storage/${job.logo}`;
    }
    return logoUrl;
  })();
  const title = job?.title || '—';
  const org = job?.company_name || '—';
  const created = formatDate(job?.created_at);
  const location = job?.city || job?.location || '';
  const salary = job?.salary_formatted || 'Non spécifié';
  const visits = job?.visits_formatted || '';
  const reference = job?.reference || job?.id || '';
  const description = job?.description || '';
  const companyDesc = job?.company_description || '';
  const contactName = job?.contact_name || '';
  const contactEmail = job?.email || '';
  const contactPhone = job?.phone || '';

  return (
    <div className="modern-job-details-page">
      {/* Hero Section */}
      <section className="job-hero-section">
        <div className="hero-background">
          <div className="hero-pattern"></div>
          <div className="hero-gradient"></div>
        </div>
        <div className="container">
          <div className="hero-navigation">
            <button 
              onClick={() => navigate(-1)}
              className="back-button"
            >
              <FaArrowLeft className="me-2" /> Retour aux offres
            </button>
          </div>
          
          {!loading && !error && job && (
            <div className="job-hero-content">
              <div className="job-hero-header">
                <div className="company-avatar-container">
                  <div className="company-avatar">
                    {logo ? (
                      <img 
                        src={logo} 
                        alt={org} 
                        className="company-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="company-logo-fallback" 
                      style={{ 
                        display: logo ? 'none' : 'flex',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: 'white',
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)'
                      }}
                    >
                      {org?.charAt(0) || 'C'}
                    </div>
                  </div>
                  <div className="avatar-glow"></div>
                </div>
                
                <div className="job-hero-info">
                  <div className="job-badges-top">
                    <span className="status-badge active">Actif</span>
                    <span className="urgency-badge">Urgent</span>
                  </div>
                  <h1 className="job-hero-title">{title}</h1>
                  <div className="company-info">
                    <FaBuilding className="company-icon" />
                    <span className="company-name">{org}</span>
                  </div>
                  
                  <div className="job-meta-hero">
                    {location && (
                      <div className="meta-item">
                        <FaMapMarkerAlt className="meta-icon" />
                        <span>{location}</span>
                      </div>
                    )}
                    {created && (
                      <div className="meta-item">
                        <FaCalendarAlt className="meta-icon" />
                        <span>Publié le {created}</span>
                      </div>
                    )}
                    {visits && (
                      <div className="meta-item">
                        <FaEye className="meta-icon" />
                        <span>{visits}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="job-hero-actions">
                  {job?.application_url ? (
                    <a 
                      href={job.application_url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="primary-apply-btn"
                    >
                      <span>Postulez</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </a>
                  ) : (
                    <button className="primary-apply-btn" onClick={handleApply}>
                      <span>Postulez</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  )}
                  
                  <button 
                    className={`save-job-btn ${isSaved ? 'saved' : ''}`}
                    onClick={handleSaveJob}
                    disabled={savingJob}
                  >
                    <FaHeart />
                    <span>
                      {savingJob ? 'Sauvegarde...' : isSaved ? 'Retirer des favoris' : 'Sauvegarder'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="job-content-section">
        <div className="container">
          
          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          )}
          
          {error && !loading && (
            <div className="error-container">
              <div className="error-message">
                <h5>Erreur de chargement</h5>
                <p>{error}</p>
              </div>
            </div>
          )}
          
          {!loading && !error && job && (
            <div className="job-content-grid">
              <div className="job-main-content">
                <div className="job-tabs-header">
                  <nav className="job-tabs-nav">
                    <button 
                      className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
                      onClick={() => setActiveTab('description')}
                    >
                      <i className="fa-regular fa-file-text me-2"></i>
                      Description de l'offre
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'company' ? 'active' : ''}`}
                      onClick={() => setActiveTab('company')}
                    >
                      <i className="fa-regular fa-building me-2"></i>
                      À propos de l'entreprise
                    </button>
                  </nav>
                </div>
                
                <div className="job-tab-content">
                  {activeTab === 'description' && (
                    <div className="job-description">
                      {formatText(description)}
                    </div>
                  )}
                  
                  {activeTab === 'company' && companyDesc && (
                    <div className="job-description">
                      <h3>À propos de {org}</h3>
                      {formatText(companyDesc)}
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="job-sidebar">
                <div className="sidebar-card">
                  <h3 className="sidebar-title">
                    <i className="fa-solid fa-info-circle me-2"></i>
                    Résumé de l'offre
                  </h3>
                  <ul className="job-summary-list">
                    {location && (
                      <li className="summary-item">
                        <span className="summary-label">
                          <FaMapMarkerAlt />
                          Localisation
                        </span>
                        <span className="summary-value">{location}</span>
                      </li>
                    )}
                    <li className="summary-item">
                      <span className="summary-label">
                        <FaBuilding />
                        {org}
                      </span>
                      <span className="summary-value"></span>
                    </li>
                 
                    <li className="summary-item">
                      <span className="summary-label">
                        <FaFileAlt />
                        Référence
                      </span>
                      <span className="summary-value">{reference}</span>
                    </li>
                    {visits && (
                      <li className="summary-item">
                        <span className="summary-label">
                          <FaEye />
                          Vues
                        </span>
                        <span className="summary-value">{visits}</span>
                      </li>
                    )}
                  </ul>
                  
                  <div className="sidebar-actions">
                    {job?.application_url ? (
                      <a 
                        href={job.application_url} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="sidebar-apply-btn"
                      >
                        <span>Postulez</span>
                        <i className="fa-solid fa-external-link"></i>
                      </a>
                    ) : (
                      <button className="sidebar-apply-btn" onClick={handleApply}>
                        <span>Postulez</span>
                        <i className="fa-solid fa-arrow-right"></i>
                      </button>
                    )}
                    
                    <button 
                      className={`sidebar-save-btn ${isSaved ? 'saved' : ''}`}
                      onClick={handleSaveJob}
                      disabled={savingJob}
                    >
                      <FaHeart />
                      <span>
                        {savingJob ? 'Sauvegarde...' : isSaved ? 'Retirer des favoris' : 'Sauvegarder cette offre'}
                      </span>
                    </button>
                  </div>
                </div>
                
                <div className="sidebar-card">
                  <h3 className="sidebar-title">
                    <i className="fa-solid fa-share-nodes me-2"></i>
                    Partager cette offre
                  </h3>
                  <div className="share-buttons">
                    <a 
                      className="share-btn facebook" 
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(job?.url || window.location.href)}`} 
                      target="_blank" 
                      rel="noreferrer"
                      title="Partager sur Facebook"
                    >
                      <FaFacebookF />
                    </a>
                    <a 
                      className="share-btn twitter" 
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(job?.url || window.location.href)}&text=${encodeURIComponent(title)}`} 
                      target="_blank" 
                      rel="noreferrer"
                      title="Partager sur Twitter"
                    >
                      <FaTwitter />
                    </a>
                    <a 
                      className="share-btn linkedin" 
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(job?.url || window.location.href)}`} 
                      target="_blank" 
                      rel="noreferrer"
                      title="Partager sur LinkedIn"
                    >
                      <FaLinkedinIn />
                    </a>
                    <a 
                      className="share-btn whatsapp" 
                      href={`whatsapp://send?text=${encodeURIComponent(title + ' ' + (job?.url || window.location.href))}`} 
                      target="_blank" 
                      rel="noreferrer"
                      title="Partager sur WhatsApp"
                    >
                      <FaWhatsapp />
                    </a>
                    <button 
                      className="share-btn copy"
                      onClick={() => {
                        navigator.clipboard.writeText(job?.url || window.location.href);
                      }}
                      title="Copier le lien"
                    >
                      <FaLink />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Similar Jobs Section */}
          <div className="similar-jobs-section">
              <h2 className="sidebar-title">
                <i className="fa-solid fa-briefcase me-2"></i>
                Offres similaires
              </h2>
              {similarJobs.length > 0 ? (
                <div className="similar-jobs-grid">
                  {similarJobs.map((similarJob) => (
                    <Link 
                      key={similarJob.id}
                      to={`/job-detail?id=${similarJob.id}`} 
                      className="similar-job-card"
                    >
                      <div className="similar-job-header">
                        <div className="similar-job-avatar">
                          {(() => {
                            let logoUrl = '';
                            if (similarJob.logo_url) {
                              // Utiliser medium en priorité
                              logoUrl = similarJob.logo_url.medium || similarJob.logo_url.small || similarJob.logo_url.large || similarJob.logo_url.full;
                            } else if (similarJob.logo) {
                              logoUrl = `https://update.senoffre.com/storage/${similarJob.logo}`;
                            }
                            
                            return logoUrl ? (
                              <img 
                                src={logoUrl} 
                                alt={similarJob.company_name}
                                style={{width: '100%', height: '100%', objectFit: 'cover'}}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = document.createElement('div');
                                  fallback.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: bold; color: white; background: linear-gradient(135deg, #22c55e, #16a34a)';
                                  fallback.textContent = (similarJob.company_name || '??').substring(0, 2).toUpperCase();
                                  e.target.parentNode.appendChild(fallback);
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: 'white',
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)'
                              }}>
                                {(similarJob.company_name || '??').substring(0, 2).toUpperCase()}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="similar-job-info">
                          <h4>{similarJob.title}</h4>
                          <div className="similar-job-company">{similarJob.company_name}</div>
                        </div>
                      </div>
                      <div className="similar-job-meta">
                        {similarJob.city && (
                          <span>
                            <FaMapMarkerAlt /> {typeof similarJob.city === 'object' ? similarJob.city.name : similarJob.city}
                          </span>
                        )}
                        {similarJob.created_at && (
                          <span><FaCalendarAlt /> {formatDate(similarJob.created_at)}</span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="error-container">
                  <p>Aucune offre similaire trouvée pour le moment.</p>
                </div>
              )}
          </div>
        </div>
      </section>
      
      {/* Container pour les toasts */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  )
};

export default JobDetails;